/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { ShowMoreList, Typography2, breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import type { DdpFieldsAboveFoldFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import { HideOnDesktop, HideOnMobile } from 'bundles/degree-description/components/shared/BreakpointDisplay';
import { useDegreeDescriptionPageElementRefs } from 'bundles/degree-description/contexts/DegreeDescriptionPageElementRefsContext';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import type { EoiTextType } from 'bundles/degree-description/utils/EoiUtils';
import { getManualCopyTest } from 'bundles/degree-description/utils/manualCopyTests';
import {
  getCurrentPersonalizedMessages,
  getMessageViewCount,
} from 'bundles/degree-description/utils/personalizedMessageViews';
import EoiButton from 'bundles/expression-of-interest/components/EoiButton';
import type { EoiButtonsType } from 'bundles/expression-of-interest/types';

import _t from 'i18n!nls/degree-description';

type Props = {
  atAGlance?: DdpFieldsAboveFoldFragment['atAGlance'];
  eoiButtons?: EoiButtonsType;
  eoiContainerText?: EoiTextType;
  mobileOnly?: boolean;
  trackingIndex?: number;
  setEoiTrackingIndex?: React.Dispatch<React.SetStateAction<number>>;
};

type AtAGlanceTitles =
  | 'bannerAtAGlanceTitle1'
  | 'bannerAtAGlanceTitle2'
  | 'bannerAtAGlanceTitle3'
  | 'bannerAtAGlanceTitle4'
  | 'bannerAtAGlanceTitle5';
type AtAGlanceDescriptions =
  | 'bannerAtAGlanceDescription1'
  | 'bannerAtAGlanceDescription2'
  | 'bannerAtAGlanceDescription3'
  | 'bannerAtAGlanceDescription4'
  | 'bannerAtAGlanceDescription5';

const styles = {
  container: css`
    padding: 0 var(--cds-spacing-200);
    margin-bottom: 0;
    ${breakpoints.up('md')} {
      background: var(--cds-color-neutral-background-primary);
      box-shadow: 0 0 40px -8px var(--cds-color-darken-200), 0 0 24px -16px var(--cds-color-darken-200);
      border-radius: 8px;
      margin-bottom: var(--cds-spacing-200);
      padding: var(--cds-spacing-200);
    }
    ${breakpoints.up('lg')} {
      padding: var(--cds-spacing-200) var(--cds-spacing-400);
    }

    .at-a-glance .cds-ShowMoreList-ctaButton {
      margin-bottom: var(--cds-spacing-100);
      margin-right: 0;
      margin-top: var(--cds-spacing-100);
    }

    .rc-Markdown.styled {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p {
        font-family: ${typography2.bodyPrimary.font};
      }

      a {
        &:hover,
        &:focus {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-interactive-primary-pressed);
        }
      }

      h2 {
        ${typography2.titleSmall};
        color: var(--cds-color-neutral-primary);
        margin: 0;
      }

      p {
        ${typography2.bodySecondary};
        color: var(--cds-color-neutral-primary-weak);
        margin: 0;
      }
    }
  `,
  buttonsArea: css`
    display: grid;
    grid-template-columns: 1fr;
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-300);

    button,
    a {
      margin-bottom: 0;
      margin-top: 0;

      &:first-child {
        margin-bottom: var(--cds-spacing-200);
      }
    }

    ${breakpoints.down('sm')} {
      margin-bottom: 0;
    }
  `,
  atAGlance: css`
    display: grid;
    grid-template-columns: 1fr;

    hr {
      margin-bottom: var(--cds-spacing-200);
      margin-top: 0;
    }

    ${breakpoints.up('md')} {
      gap: var(--cds-spacing-50);
    }
  `,
  atAGlanceItem: css`
    margin-bottom: var(--cds-spacing-300);

    &:last-child {
      margin-bottom: var(--cds-spacing-200);
    }

    ${breakpoints.down('sm')} {
      border-bottom: 2px solid var(--cds-color-neutral-stroke-primary-weak);
      margin-bottom: 0;
      padding: var(--cds-spacing-150) 0;
    }
  `,
};

const EoiAtAGlanceBlock = ({
  atAGlance,
  eoiButtons,
  eoiContainerText,
  mobileOnly,
  trackingIndex,
  setEoiTrackingIndex,
}: Props) => {
  const { setFloatingEoiButtonsContainerRef } = useDegreeDescriptionPageElementRefs();
  const { useGetViewPageSectionRef, trackingProductId } = useDegreeDescriptionEventingContext();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const pageSectionRef = useGetViewPageSectionRef('eoi', trackingIndex);
  const { slug } = useParams();

  // DDP Manual Copy Tests
  const manualCopyTestData = slug ? getManualCopyTest(slug) : undefined;

  const atAGlanceItems = React.useMemo(() => {
    let atAGlanceArray: Array<{ title?: string | null; description?: string | null }> = [];

    if (atAGlance) {
      if (!manualCopyTestData || manualCopyTestData?.manualCopyTestType !== 'atAGlance') {
        for (let i = 1; i <= 5; i += 1) {
          let currTitle = atAGlance[`bannerAtAGlanceTitle${i}` as AtAGlanceTitles];
          let currDescription = atAGlance[`bannerAtAGlanceDescription${i}` as AtAGlanceDescriptions];

          if (manualCopyTestData?.manualCopyTestType === 'tuition' && manualCopyTestData.position === i) {
            currTitle = manualCopyTestData.title;
            currDescription = manualCopyTestData.description;
          }

          atAGlanceArray.push({ title: currTitle, description: currDescription });
        }
      } else if (manualCopyTestData?.manualCopyTestType === 'atAGlance') {
        atAGlanceArray = manualCopyTestData.atAGlance;
      }
    }
    return atAGlanceArray.map((item) => {
      return (
        (item.title || item.description) && (
          <div css={styles.atAGlanceItem} key={item.title}>
            {item.title && (
              <Typography2 variant="subtitleLarge" component="h2">
                {item.title}
              </Typography2>
            )}
            {item.description && <Markdown source={item.description} />}
          </div>
        )
      );
    });
  }, [atAGlance, manualCopyTestData]);

  return (
    <div css={styles.container} data-testid="eoi-container">
      {!isMobile && (
        <div ref={pageSectionRef}>
          {eoiContainerText?.programInterestText && (
            <div className="eoi-header-text">
              <Markdown source={eoiContainerText.programInterestText} />
            </div>
          )}
          {eoiButtons && (
            <div
              css={styles.buttonsArea}
              /** since this component is rendered twice on the overview page we want to
               * only set the floatingEoiButtonsContainerRef if this component is visible which happens when:
               * 1. this component is only being rendered on mobile and the browser is currently on a mobile viewport
               * 2. this component is not rendered on mobile and the browser is not on a mobile viewport
               */
              ref={(e) => {
                if ((mobileOnly && isMobile) || (!mobileOnly && !isMobile))
                  setFloatingEoiButtonsContainerRef?.(e as HTMLElement);
              }}
            >
              {eoiButtons.map((eoiButton) => {
                const eoiButtonWithPersonalization: typeof eoiButton = {
                  ...eoiButton,
                  buttonOptions: {
                    ...eoiButton.buttonOptions,
                    trackingData: {
                      ...eoiButton.buttonOptions?.trackingData,
                      currentPersonalizedMessages: getCurrentPersonalizedMessages(),
                      personalizedMessageViewCount: getMessageViewCount(),
                    },
                  },
                };

                return (
                  <EoiButton
                    actionConfig={eoiButtonWithPersonalization}
                    expressionOfInterestText={eoiContainerText}
                    key={`${eoiButton.actionType}-eoi-overview-button`}
                    trackingIndex={trackingIndex}
                    setEoiTrackingIndex={setEoiTrackingIndex}
                    trackingProductId={trackingProductId}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
      {atAGlance && (
        <div className="at-a-glance">
          <div css={styles.atAGlance}>
            <React.Fragment>
              <HideOnMobile>
                <React.Fragment>
                  <hr />
                  {atAGlanceItems}
                </React.Fragment>
              </HideOnMobile>
              <HideOnDesktop>
                <ShowMoreList
                  numItemsToShow={2}
                  controllerProps={{
                    ariaLabel: _t('At-A-Glance program information'),
                    collapsedLabel: _t('View More'),
                    expandedLabel: _t('View Less'),
                  }}
                >
                  {atAGlanceItems}
                </ShowMoreList>
              </HideOnDesktop>
            </React.Fragment>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(EoiAtAGlanceBlock);
